import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import validator from "validator";
import {
  createEnvelopeRecipient,
  updateRecipient,
  getCountryList,
  getCountryByOrder,
} from "../../../../services/CommonService";
import {
  COUNTRY_CODE,
  COUNTRY_ICON,
  COUNTRY_ID,
} from "../../../../configs/AppConfig";
import Utils from "../../../../utils";

const NegotiationRecipient = ({ formObj, setFormObj, recipData, setLoading }) => {
  const clsButtonRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const [countryList, setCountryList] = useState([]);
  const [countryCodeList, setCountryCodeList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [form, setForm] = useState({
    id: 0,
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    country_id: COUNTRY_ID,
    company_name: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zip_code: "",
    address_country_id: "",
  });

  const [countryIcon, setCountryIcon] = useState(COUNTRY_ICON);
  const [countryCode, setCountryCode] = useState(COUNTRY_CODE);
  const [errors, setErrors] = useState({});
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  useEffect(() => {
    getCountryByOrder("code")
      .then((res) => setCountryCodeList(res.data.data))
      .catch(() => toast.error("Failed to load country codes"));
    getCountryList()
      .then((res) => setCountryList(res.data.data))
      .catch(() => toast.error("Failed to load country list"));
  }, []);

  useEffect(() => {
    if (recipData && Object.keys(recipData).length > 0) {
      setForm({
        id: recipData.id || 0,
        first_name: recipData.first_name || "",
        last_name: recipData.last_name || "",
        email: recipData.email || "",
        mobile: recipData.mobile || "",
        country_id: recipData.country_id || COUNTRY_ID,
        company_name: recipData.company_name || "",
        address_1: recipData.address_1 || "",
        address_2: recipData.address_2 || "",
        city: recipData.city || "",
        state: recipData.state || "",
        zip_code: recipData.zip_code || "",
        address_country_id: recipData.address_country_id || "",
      });

      // FIX: Ensure safe parsing and finding of country code
      if (recipData.country_id && countryCodeList.length > 0) {
        const selected = countryCodeList.find(
          (x) => x.id === parseInt(recipData.country_id)
        );
        if (selected) {
          setCountryIcon(selected.icon?.toLowerCase() || COUNTRY_ICON);
          setCountryCode(selected.code || COUNTRY_CODE);
        }
      }
    } else {
      clearForm();
    }
  }, [recipData, countryCodeList]); // FIX: Dependency array updated to run when list loads

  const clearForm = () => {
    setForm({
      id: 0,
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      country_id: COUNTRY_ID,
      company_name: "",
      address_1: "",
      address_2: "",
      city: "",
      state: "",
      zip_code: "",
      address_country_id: "",
    });
    setErrors({});
    setCountryIcon(COUNTRY_ICON);
    setCountryCode(COUNTRY_CODE);
    setSearchTerm("");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.first_name) newErrors.first_name = "First name required";
    if (!form.last_name) newErrors.last_name = "Last name required";
    
    // FIX: Safer email check. Convert to string to avoid crash if null
    if (!form.email) {
      newErrors.email = "Email required";
    } else if (!validator.isEmail(String(form.email))) {
      newErrors.email = "Invalid email";
    }

    // Mobile number validation - exactly 10 digits
    if (form.mobile) {
      const mobileDigits = form.mobile.replace(/\D/g, '');
      if (mobileDigits.length !== 10) {
        newErrors.mobile = "Mobile number must be 10 digits";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCountry = (e, obj) => {
    e.preventDefault();
    const selected = countryCodeList.find((x) => x.id === parseInt(obj.id));
    if (selected) {
      setCountryIcon(selected.icon.toLowerCase());
      setCountryCode(selected.code);
    }
    setForm({ ...form, country_id: obj.id });
    setSearchTerm("");
  };

  const onStoreRecipient = (e, type) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      envelope_id: formObj?.envelope_id,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      mobile: form.mobile,
      country_id: form.country_id,
      company_name: form.company_name,
      address_1: form.address_1,
      address_2: form.address_2,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      address_country_id: form.address_country_id,
    };

    setLoading(true);
    createEnvelopeRecipient(payload)
      .then((response) => {
        // FIX: IMMUTABLE STATE UPDATE (Don't use push)
        const formData = { ...formObj };
        const currentList = Array.isArray(formData.recipient_List) 
          ? formData.recipient_List 
          : [];
        
        formData.recipient_List = [...currentList, response.data.data];
        
        setFormObj(formData);
        toast.success(response.data.message);
        if (type === 1) clsButtonRef.current?.click();
        clearForm();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };

  const onUpdateRecipient = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      recipient_id: form.id,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      mobile: form.mobile,
      country_id: form.country_id,
      company_name: form.company_name,
      address_1: form.address_1,
      address_2: form.address_2,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      address_country_id: form.address_country_id,
    };

    setLoading(true);
    updateRecipient(payload)
      .then((response) => {
        // FIX: IMMUTABLE STATE UPDATE
        const formData = { ...formObj };
        const updatedList = [...(formData.recipient_List || [])];
        
        const index = updatedList.findIndex((x) => x.id === form.id);
        if (index > -1) {
          // Merge old data with new form data
          updatedList[index] = { ...updatedList[index], ...form };
        }
        
        formData.recipient_List = updatedList;
        setFormObj(formData);
        
        toast.success(response.data.message);
        clsButtonRef.current?.click();
        clearForm();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };

  return (
    <div
      className="offcanvas offcanvas-end Add-Recipients bg-light"
      data-bs-scroll="true"
      tabIndex="-1"
      data-bs-keyboard="false"
      data-bs-backdrop="static"
      id="addRecipientsnegotiation"
      aria-labelledby="addRecipientsnegotiation"
    >
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">
          {form.id ? "Edit Collaborator" : "Add Collaborator"}
        </h5>
        <button
          type="button"
          ref={clsButtonRef}
          className="btn close_btn text-reset"
          data-bs-dismiss="offcanvas"
        >
          <i className="bi bi-x-circle" />
        </button>
      </div>

      <div className="offcanvas-body">
        {/* Basic Info */}
        <div className="row g-3">
          <div className="col-lg-6">
            <label className="form-label">First Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              value={form.first_name}
              onChange={(e) =>
                setForm({ ...form, first_name: e.target.value }) 
              }
              onBlur={(e) => setForm({ ...form, first_name: e.target.value.trim() })} 
              required
            />
            {errors.first_name && (
              <div className="text-danger">{errors.first_name}</div>
            )}
          </div>
          <div className="col-lg-6">
            <label className="form-label">Last Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              onBlur={(e) => setForm({ ...form, last_name: e.target.value.trim() })}
              required
            />
            {errors.last_name && (
              <div className="text-danger">{errors.last_name}</div>
            )}
          </div>
          <div className="col-lg-6">
            <label className="form-label">Email <span className="text-danger">*</span></label>
            <input
              type="email"
              className="form-control"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              onBlur={(e) => setForm({ ...form, email: e.target.value.trim() })}
              required
            />
            {errors.email && <div className="text-danger">{errors.email}</div>}
          </div>
          <div className="col-lg-6">
            <label className="form-label">Mobile Number</label>
            <div className="mobile-input-group">
              <div className="dropdown">
                <button
                  className="country-code-btn dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={`/images/flags/${countryIcon}.png`}
                      className="me-2"
                      width="20"
                      alt="flag"
                    />
                    <span>+{countryCode}</span>
                  </div>
                </button>
                <ul
                  className="dropdown-menu p-2 country_code-dropdown"
                  ref={countryDropdownRef}
                >
                  <li className="mb-2">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search"
                      value={searchTerm}
                      // FIX: Stop propagation to prevent dropdown close on click
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </li>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {countryCodeList
                    .filter(
                      (country) =>
                        country.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        country.code.toString().includes(searchTerm)
                    )
                    .map((item) => (
                      // FIX: Use Unique ID for key, not index
                      <li key={item.id} onClick={(e) => handleCountry(e, item)}>
                        <div className="dropdown-item d-flex align-items-center cursor-pointer">
                          <img
                            src={`/images/flags/${item.icon}.png`}
                            width="20"
                            className="me-2"
                            alt={item.name}
                          />
                          {item.name}
                          <span className="ms-auto">+{item.code}</span>
                        </div>
                      </li>
                    ))}
                  </div>
                </ul>
              </div>
              <input
                type="tel"
                className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                value={form.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setForm({ ...form, mobile: value });
                  // Clear error immediately if length becomes valid (optional but better UX)
                  if (errors.mobile && value.length === 10) {
                    setErrors((prev) => {
                      const newErr = { ...prev };
                      delete newErr.mobile;
                      return newErr;
                    });
                  }
                }}
                onBlur={() => {
                  if (form.mobile && form.mobile.length !== 10) {
                    setErrors((prev) => ({ ...prev, mobile: "Mobile number must be 10 digits" }));
                  }
                }}
                placeholder="Mobile Number"
                maxLength={10}
              />
            </div>
            {errors.mobile && <div className="text-danger mt-1">{errors.mobile}</div>}
          </div>
        </div>

        {/* Accordion - More Info */}
        <div className="accordion mt-4" id="accordionOptional">
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${
                  isAccordionOpen ? "" : "collapsed"
                }`}
                type="button"
                onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              >
                More Information
              </button>
            </h2>
            <div
              id="collapseOptional"
              className={`accordion-collapse collapse ${
                isAccordionOpen ? "show" : ""
              }`}
            >
              <div className="accordion-body">
                <div className="row g-3">
                  <div className="col-lg-6">
                    <label className="form-label">Company Name</label>
                    <input
                      className="form-control"
                      value={form.company_name}
                      onChange={(e) =>
                        setForm({ ...form, company_name: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Address Line 1</label>
                    <input
                      className="form-control"
                      value={form.address_1}
                      onChange={(e) =>
                        setForm({ ...form, address_1: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Address Line 2</label>
                    <input
                      className="form-control"
                      value={form.address_2}
                      onChange={(e) =>
                        setForm({ ...form, address_2: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">City</label>
                    <input
                      className="form-control"
                      value={form.city}
                      onChange={(e) =>
                        setForm({ ...form, city: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">State</label>
                    <input
                      className="form-control"
                      value={form.state}
                      onChange={(e) =>
                        setForm({ ...form, state: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">Country</label>
                    <select
                      className="form-select"
                      value={form.address_country_id}
                      onChange={(e) =>
                        setForm({ ...form, address_country_id: e.target.value })
                      }
                    >
                      <option value="">Select Country</option>
                      {countryList.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">Zip Code</label>
                    <input
                      className="form-control"
                      value={form.zip_code}
                      onChange={(e) =>
                        setForm({ ...form, zip_code: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="modal-footer py-2 border-top gap-2">
        <button
          type="button"
          onClick={() => clsButtonRef.current?.click()}
          className="btn btn-outline-secondary"
        >
          Cancel
        </button>
        {form.id ? (
          <button className="btn btn-primary" onClick={onUpdateRecipient}>
            Update
          </button>
        ) : (
          <>
            <button
              className="btn btn-outline-primary"
              onClick={(e) => onStoreRecipient(e, 2)}
            >
              Save & Add Another
            </button>
            <button
              className="btn btn-primary"
              onClick={(e) => onStoreRecipient(e, 1)}
            >
              Save
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default NegotiationRecipient;